import { Cancel, CheckCircle, NotificationsActive, Schedule } from '@mui/icons-material'
import { Button, Box } from '@mui/material'
import { useNotification, useUpdate } from '@refinedev/core'
import React, { useState } from 'react'

import type { Scout } from '../../../scouts'
import { useRetriggerScoutNotificationsMutation } from '../../api'
import type { Appointment } from '../../types.ts'

import CancelAppointmentDialog from './CancelAppointmentDialog'
import CompleteAppointmentDialog from './CompleteAppointmentDialog'
import ManualNotifyDialog from './ManualNotifyDialog'
import ManualScheduleDialog from './ManualScheduleDialog'
import RetriggerNotificationsDialog from './RetriggerNotificationsDialog'

interface Props {
  appointment: Appointment
  onRefetch: () => void
}

export const ActiveAppointmentActions = ({ appointment, onRefetch }: Props) => {
  const [manualNotifyDialogOpen, setManualNotifyDialogOpen] = React.useState(false)
  const [manualScheduleDialogOpen, setManualScheduleDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false)
  const [retriggerDialogOpen, setRetriggerDialogOpen] = useState(false)
  const { open } = useNotification()

  const { mutate: updateAppointment } = useUpdate<Appointment>()
  const { mutate: retriggerNotifications } = useRetriggerScoutNotificationsMutation()

  const handleManualSchedule = (scout: Scout, dateTime: string) => {
    const scoutName = `${scout.firstName} ${scout.lastName}`

    const updateData = {
      scoutUUID: scout.uuid,
      scoutName: scoutName,
      scheduleStartTime: dateTime,
      status: 'dispatched',
    }

    console.error('Updating appointment with data:', updateData)

    updateAppointment(
      {
        resource: 'appointments',
        id: appointment.uuid as string,
        values: updateData,
        successNotification: {
          message: 'Appointment scheduled successfully',
          type: 'success',
        },
      },
      {
        onSuccess: () => {
          onRefetch()
        },
        onError: error => {
          console.error('Error updating appointment:', error)
        },
      }
    )
  }

  const handleRetriggerNotifications = () => {
    if (!appointment?.uuid) return

    retriggerNotifications(appointment?.uuid, {
      onSuccess: data => {
        if (data.errors && data.errors.length > 0) {
          open?.({
            type: 'error',
            message: 'Some notifications failed to send',
            description: data.errors.join(', '),
          })
        } else {
          open?.({
            type: 'success',
            message: 'Scout notifications retriggered successfully',
          })
        }

        onRefetch()
      },
      onError: error => {
        console.error('Error retriggering notifications:', error)
        open?.({
          type: 'error',
          message: 'Failed to retrigger scout notifications',
          description: error.message,
        })
      },
    })
  }

  const handleCancel = () => {
    updateAppointment(
      {
        resource: 'appointments',
        id: appointment.uuid as string,
        values: {
          status: 'cancelled',
          sendEmails: true,
        },
        successNotification: {
          message: 'Appointment cancelled successfully',
          type: 'success',
        },
      },
      {
        onSuccess: () => {
          setCancelDialogOpen(false)
          onRefetch()
        },
      }
    )
  }

  const handleComplete = () => {
    updateAppointment(
      {
        resource: 'appointments',
        id: appointment.uuid as string,
        values: { status: 'completed' },
        successNotification: {
          message: 'Appointment marked as completed',
          type: 'success',
        },
      },
      {
        onSuccess: () => {
          setCompleteDialogOpen(false)
          onRefetch()
        },
      }
    )
  }

  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      <Button
        variant="contained"
        startIcon={<Schedule />}
        onClick={() => setManualScheduleDialogOpen(true)}
        size="small"
      >
        Manually Schedule
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<NotificationsActive />}
        onClick={() => setManualNotifyDialogOpen(true)}
        size="small"
      >
        Manually Notify
      </Button>
      {appointment.status === 'new' && (
        <Button
          variant="outlined"
          color="warning"
          startIcon={<NotificationsActive />}
          onClick={() => setRetriggerDialogOpen(true)}
          size="small"
        >
          Retrigger Notifications
        </Button>
      )}
      <Button
        variant="outlined"
        color="error"
        startIcon={<Cancel />}
        onClick={() => setCancelDialogOpen(true)}
        size="small"
      >
        Cancel
      </Button>
      {(appointment.status === 'dispatched' || appointment.status === 'new') && (
        <Button
          variant="outlined"
          color="success"
          startIcon={<CheckCircle />}
          onClick={() => setCompleteDialogOpen(true)}
          size="small"
        >
          Complete
        </Button>
      )}

      {appointment?.uuid && (
        <ManualNotifyDialog
          open={manualNotifyDialogOpen}
          onClose={() => setManualNotifyDialogOpen(false)}
          appointmentId={appointment.uuid}
          appointmentDetails={{
            customerName: appointment.customerName || undefined,
            customerAddress: appointment.customerAddress || undefined,
          }}
        />
      )}

      <RetriggerNotificationsDialog
        open={retriggerDialogOpen}
        onClose={() => setRetriggerDialogOpen(false)}
        onConfirm={handleRetriggerNotifications}
        appointmentId={appointment?.uuid}
        customerName={appointment?.customerName}
      />

      <ManualScheduleDialog
        open={manualScheduleDialogOpen}
        onClose={() => setManualScheduleDialogOpen(false)}
        onConfirm={handleManualSchedule}
        customerName={appointment?.customerName}
        proposedDates={[
          appointment?.proposedDate1 ?? null,
          appointment?.proposedDate2 ?? null,
          appointment?.proposedDate3 ?? null,
        ]}
        proposedTimeRanges={[
          appointment?.proposedTimeRange1 ?? null,
          appointment?.proposedTimeRange2 ?? null,
          appointment?.proposedTimeRange3 ?? null,
        ]}
      />

      <CancelAppointmentDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancel}
        appointmentId={appointment?.uuid}
        customerName={appointment?.customerName}
        scheduleStartTime={appointment?.scheduleStartTime}
      />

      <CompleteAppointmentDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        onConfirm={handleComplete}
        appointmentId={appointment?.uuid}
        customerName={appointment?.customerName}
      />
    </Box>
  )
}
